import { ampli } from '@/ampli';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import qs from 'query-string';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDebounce, useMedia } from 'react-use';
import { toast } from 'sonner';
import { Chain } from 'viem';
import { z } from 'zod';

import { ClientNetworkError } from '@/lib/errors/client';

import {
  COMPLETED_TRADE_COLUMN_CLASS,
  getCompletedTradeColumns,
  toCompletedTradeRow,
} from '@/components/TradeHistoryTable/columns/completedTradeColumns';
import { TableColumn } from '@/components/TradeHistoryTable/config';
import { DataTable } from '@/components/TradeHistoryTable/DataTable';
import { TableSkeleton } from '@/components/TradeHistoryTable/TableSkeleton';
import type { TradeDetailsSelection } from '@/components/TradeHistoryTable/types';

import { isAdvancedClosedOrderStatus } from '@/constants/advancedOrder';
import {
  CROSS_CHAIN_SUPPORTED_CHAINS,
  LIMIT_ORDER_SUPPORTED_CHAINS,
  NETWORK_NAME_PER_CHAIN_ID,
  SUPPORTED_CHAINS,
} from '@/constants/chain';

import { useTradeHistoryTokenResults } from '@/hooks/tokenQueries/useTradeHistoryTokenResults';
import { usePagination } from '@/hooks/usePagination';
import { useLastPage } from '@/hooks/usePagination';
import { type LimitOrderFunders, useWalletTradeHistory } from '@/hooks/useWalletOpenLimitOrders';

import { transactionsSelector, useTransactionStore } from '@/store/transaction';
import { type Transaction, TransactionsByChain } from '@/store/transaction/types';

import { buttons } from '@/styles/buttons';
import { breakpoints } from '@/styles/util';

import { Modal } from '@/ui/Modal';
import { Pagination } from '@/ui/Pagination';
import { Tooltip } from '@/ui/Tooltip';

import { singleAddressTradeHistorySchema } from '@/utils/0x/history/schema';
import { trackError } from '@/utils/errors';
// utils
import { routes } from '@/utils/routes';

import { SearchBar } from '..';
import { DIVIDER_CHAR, PAGE_SIZE } from '..';
import { parseHistoryChainId } from '../utils';
import {
  Desktop as DesktopFilters,
  Mobile as MobileFilters,
  DEFAULT_FILTERS_STATE,
  filtersReducer,
  FILTER_ACTIONS,
} from './Filters';
import { FiltersReducerState } from './Filters/reducer';
import { mergeCachedTransactionsIntoTrades, RECENT_TRANSACTION_WINDOW_MS } from './utils';

/**
 * Search for the search term in the transaction details
 * @param searchTerm - Search term to match
 * @param tx - Transaction to search in
 * @returns True if the search term is partially found
 */
const matchSearchTerm = (searchTerm: string, tx: Transaction): boolean => {
  const cleanSearchTerm = searchTerm.trim().toLowerCase();
  const sellNetworkName = NETWORK_NAME_PER_CHAIN_ID[tx.sellToken.chainId] ?? '';
  const buyNetworkName = NETWORK_NAME_PER_CHAIN_ID[tx.buyToken.chainId] ?? '';
  return (
    tx.sellToken.name.toLowerCase().includes(cleanSearchTerm) ||
    tx.buyToken.name.toLowerCase().includes(cleanSearchTerm) ||
    tx.sellToken.symbol.toLowerCase().includes(cleanSearchTerm) ||
    tx.buyToken.symbol.toLowerCase().includes(cleanSearchTerm) ||
    tx.sellToken.address.toLowerCase().includes(cleanSearchTerm) ||
    tx.buyToken.address.toLowerCase().includes(cleanSearchTerm) ||
    sellNetworkName.toLowerCase().includes(cleanSearchTerm) ||
    buyNetworkName.toLowerCase().includes(cleanSearchTerm)
  );
};

interface OrderHistoryProps {
  funders: LimitOrderFunders;
  /** The order type to display */
  orderType: 'market' | 'limit' | 'cross-chain' | 'swaps';
  /** Function to show the trade details modal. */
  handleShowTradeDetails: (selectedTrade: TradeDetailsSelection) => void;
  /** Initial page number. */
  initialPage: number | undefined;
  /** Initial filters. */
  initialFilters: FiltersReducerState | undefined;
  /** Initial search term. */
  initialSearchTerm: string | undefined;
  /** Callback on page change. */
  onPageChange: (page: number) => void;
  /** Callback on filters change. */
  onFiltersChange: (filters: FiltersReducerState) => void;
  /** Callback on search term change. */
  onSearchTermChange: (searchTerm: string) => void;
  /** Callback on filters reset. */
  onFiltersReset: () => void;
}

const MARKET_TABLE_COLUMNS: TableColumn[] = [
  { key: 'volume', label: 'Volume' },
  { key: 'sellAmount', label: 'Sell' },
  { key: 'buyAmount', label: 'Buy' },
  { key: 'network', label: 'Network' },
  { key: 'date', label: 'Date' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
];

const LIMIT_TABLE_COLUMNS: TableColumn[] = [
  { key: 'volume', label: 'Volume' },
  { key: 'sellAmount', label: 'Sell' },
  { key: 'buyAmount', label: 'Buy' },
  { key: 'filled', label: 'Filled' },
  { key: 'network', label: 'Network' },
  { key: 'date', label: 'Date' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
];

const ORDER_TYPE_NETWORKS: Record<string, Chain[]> = {
  'market': SUPPORTED_CHAINS,
  'limit': LIMIT_ORDER_SUPPORTED_CHAINS,
  'cross-chain': CROSS_CHAIN_SUPPORTED_CHAINS,
  'swaps': SUPPORTED_CHAINS,
};

function downloadBlob(content: string, filename: string, contentType: string) {
  // Create a blob
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);

  // Create a link to download it
  const el = document.createElement('a');
  el.href = url;
  el.setAttribute('download', filename);
  el.click();
}

/**
 * Component to display a users trade history for market, limit, and cross-chain
 * orders.
 */
export const OrderHistory = ({
  funders,
  orderType,
  handleShowTradeDetails,
  initialPage,
  initialFilters,
  initialSearchTerm,
  onPageChange: handleOnPageChange,
  onFiltersChange,
  onSearchTermChange,
  onFiltersReset,
}: OrderHistoryProps) => {
  const connectedAddress = funders.ethereum ?? funders.solana;
  if (!connectedAddress) {
    throw new Error('Order history requires a connected wallet');
  }
  const accountAddress = connectedAddress;
  const orderTypeNetworks = ORDER_TYPE_NETWORKS[orderType];
  // state
  const [filterState, dispatch] = useReducer(
    filtersReducer,
    initialFilters ?? DEFAULT_FILTERS_STATE,
  );
  const filterStateRef = useRef(filterState);
  filterStateRef.current = filterState;
  const isSyncingSearchFromQueryRef = useRef(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string | undefined>(
    initialSearchTerm,
  );
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
      // Skip URL push when the search term was synced from the URL (back/forward navigation)
      // to avoid redundant router.push calls that interfere with navigation
      if (!isSyncingSearchFromQueryRef.current) {
        onSearchTermChange(searchTerm ?? '');
      }
      isSyncingSearchFromQueryRef.current = false;
    },
    250,
    [searchTerm],
  );
  const paginationProps = usePagination({
    pageSize: PAGE_SIZE,
    initialPage,
    onPageChange: handleOnPageChange,
  });
  const transactions = useTransactionStore(transactionsSelector);
  const cachedTxsRaw = funders.ethereum ? transactions[funders.ethereum] : undefined;
  const isMobile = useMedia(breakpoints.mobile, false);

  const cachedTxs = useMemo(() => {
    if (!cachedTxsRaw) return;

    const cachedTxs: TransactionsByChain = {};
    Object.keys(cachedTxsRaw).forEach((chainId) => {
      const chainIdNumber = Number(chainId);
      cachedTxs[chainIdNumber] = [];
      cachedTxsRaw[chainIdNumber]?.forEach((tx) => {
        cachedTxs[chainIdNumber]?.push(tx);
      });
    });

    return cachedTxs;
  }, [cachedTxsRaw]);

  // vars
  const {
    currentPage,
    offset,
    onPageChange,
    onFirst: selectFirstPage,
    setCurrentPage,
    setOffset,
  } = paginationProps;
  const { orderTypes, networks, status, time } = filterState;
  const showReset = orderTypes.length || networks.length || !!status || !!time;
  const startTimestamp = useMemo(() => {
    if (!!time) {
      switch (time) {
        case 'ytd':
          return new Date(new Date().getFullYear(), 0, 1).toISOString();
        case 'previous_year':
          return new Date(new Date().getFullYear() - 1, 0, 1).toISOString();
        default:
          return new Date(Date.now() - time * 60 * 1000).toISOString();
      }
    }
  }, [time]);
  const endTimestamp = useMemo(() => {
    if (time === 'previous_year') {
      return new Date(new Date().getFullYear(), 0, 1).toISOString();
    }
  }, [time]);

  const historyFilters = useMemo(
    () => ({
      chainIds: networks.length ? networks : undefined,
      orderTypes: orderType,
      advancedOrderStatus: orderType === 'limit' ? status : undefined,
      searchTerm: debouncedSearchTerm || undefined,
      startTimestamp,
      endTimestamp,
    }),
    [debouncedSearchTerm, networks, orderType, startTimestamp, endTimestamp, status],
  );
  const { data, isLoading, isFetching } = useWalletTradeHistory({
    funders,
    filters: historyFilters,
    offset,
    limit: PAGE_SIZE,
  });
  const query = useMemo(
    () =>
      '?' +
      qs.stringify({
        address: accountAddress,
        ...historyFilters,
        limit: PAGE_SIZE,
        offset,
      } satisfies z.infer<typeof singleAddressTradeHistorySchema>),
    [accountAddress, historyFilters, offset],
  );
  const { trades: tradesData, count: totalItems } = data ?? {};
  const isWaitingForPageData = !data && (isLoading || isFetching);
  const { lastPage } = useLastPage({ itemsPerPage: PAGE_SIZE, totalItems: totalItems ?? 0 });
  const trades = useMemo(() => {
    // merge recently cached transactions on the first page only
    if (!tradesData || !cachedTxs || currentPage !== 1) return [...(tradesData || [])];

    return mergeCachedTransactionsIntoTrades(tradesData, cachedTxs, {
      recencyMs: RECENT_TRANSACTION_WINDOW_MS,
      accept: (tx) => {
        const txType =
          tx.transactionType === 'gasless' ||
          tx.transactionType === 'intents' ||
          tx.transactionType === 'swap' ||
          tx.transactionType === 'batch-swap' ||
          // This currently only applies to cached transactions as wraps/unwraps are not yet handled by tx history
          // TODO @DennisSimon: Verify this is working as expected once data team ships support for wraps/unwraps
          tx.transactionType === 'wrap'
            ? 'market'
            : tx.transactionType;
        const isMatchingOrderType =
          orderType === 'swaps'
            ? txType === 'market' || txType === 'cross-chain'
            : txType === orderType;
        if (!isMatchingOrderType) return false;
        if (status && status !== 'filled') return false;

        const sellChainId = parseHistoryChainId(tx.sellToken.chainId);
        const buyChainId = parseHistoryChainId(tx.buyToken.chainId);
        if (
          networks.length &&
          !networks.includes(String(sellChainId)) &&
          (!buyChainId || !networks.includes(String(buyChainId)))
        ) {
          return false;
        }

        return !debouncedSearchTerm || matchSearchTerm(debouncedSearchTerm, tx);
      },
    });
  }, [tradesData, cachedTxs, currentPage, orderType, networks, debouncedSearchTerm, status]);

  const { getTradeTokenResults } = useTradeHistoryTokenResults(trades);
  const rows = useMemo(
    () => trades.map((trade) => toCompletedTradeRow(trade, getTradeTokenResults(trade))),
    [trades, getTradeTokenResults],
  );
  const columns = useMemo(
    () =>
      getCompletedTradeColumns({
        showFilled: orderType === 'limit',
        showType: true,
        showVolume: true,
      }),
    [orderType],
  );
  const tableColumns = orderType === 'limit' ? LIMIT_TABLE_COLUMNS : MARKET_TABLE_COLUMNS;
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const onReset = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    dispatch({ type: FILTER_ACTIONS.CLEAR });
    selectFirstPage();
    onFiltersReset();
  };

  const router = useRouter();
  const queryTime = typeof router.query.time === 'string' ? router.query.time : undefined;
  const queryNetworks =
    typeof router.query.networks === 'string' ? router.query.networks : undefined;
  const queryOrderTypes =
    typeof router.query.orderTypes === 'string' ? router.query.orderTypes : undefined;
  const queryAdvancedOrderStatus =
    typeof router.query.advancedOrderStatus === 'string'
      ? router.query.advancedOrderStatus
      : undefined;
  const querySearchTerm =
    typeof router.query.searchTerm === 'string' ? router.query.searchTerm : undefined;
  const queryPage = typeof router.query.page === 'string' ? router.query.page : undefined;

  // Sync local filter state from URL query parameters (handles shallow routing)
  useEffect(() => {
    const timeValue: FiltersReducerState['time'] = !queryTime
      ? 0
      : queryTime === 'ytd' || queryTime === 'previous_year'
        ? queryTime
        : Number(queryTime) || 0;
    const networksValue = !queryNetworks
      ? []
      : queryNetworks.split(DIVIDER_CHAR).filter((n) => !!n);
    const orderTypesValue = !queryOrderTypes
      ? []
      : queryOrderTypes.split(DIVIDER_CHAR).filter((n) => !!n);
    const statusValue =
      orderType === 'limit' && isAdvancedClosedOrderStatus(queryAdvancedOrderStatus)
        ? queryAdvancedOrderStatus
        : undefined;

    // Filter networks to only include those supported by the current order type
    const supportedNetworkIds = orderTypeNetworks.map((chain) => chain.id.toString());
    const filteredNetworksValue = networksValue.filter((networkId) =>
      supportedNetworkIds.includes(networkId),
    );

    const filtersFromQuery = {
      time: timeValue,
      networks: filteredNetworksValue,
      orderTypes: orderTypesValue,
      status: statusValue,
    };

    // Only update if filters are different from current state
    const current = filterStateRef.current;
    if (
      current.time !== filtersFromQuery.time ||
      JSON.stringify(current.networks) !== JSON.stringify(filtersFromQuery.networks) ||
      JSON.stringify(current.orderTypes) !== JSON.stringify(filtersFromQuery.orderTypes) ||
      current.status !== filtersFromQuery.status
    ) {
      dispatch({ type: FILTER_ACTIONS.UPDATE_ALL_FILTERS, payload: filtersFromQuery });
    }
  }, [
    queryTime,
    queryNetworks,
    queryOrderTypes,
    queryAdvancedOrderStatus,
    dispatch,
    orderType,
    orderTypeNetworks,
  ]);

  // Sync local search term from URL query parameters (handles shallow routing)
  useEffect(() => {
    const normalized = querySearchTerm || '';

    setSearchTerm((prev) => {
      if ((prev || '') !== normalized) {
        isSyncingSearchFromQueryRef.current = true;
        return normalized;
      }
      return prev;
    });
  }, [querySearchTerm]);

  // Sync local pagination state from URL query parameters (handles shallow routing).
  useEffect(() => {
    const parsed = Number(queryPage);
    const nextPage = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
      setOffset((nextPage - 1) * PAGE_SIZE);
    }
  }, [queryPage, currentPage, setCurrentPage, setOffset]);

  useEffect(() => {
    // update pagination when the amount of orders is updated
    if (isLoading) return;
    if (currentPage > lastPage) onPageChange(lastPage);
  }, [isLoading, lastPage, currentPage, onPageChange]);

  async function handleDownloadCsv() {
    ampli.exportTradesButtonClicked({
      address: accountAddress.toLowerCase(),
      count: totalItems ?? 0,
    });

    try {
      const response = await fetch(`${routes.api.TRADE_HISTORY_EXPORT}${query}`);

      if (!response.ok) {
        throw new ClientNetworkError(await response.text());
      }
      const csvData = await response.text();

      downloadBlob(csvData, 'matcha_trades.csv', 'text/csv;charset=utf-8;');
    } catch (err: unknown) {
      toast.error('Failed to download CSV');
      if (err instanceof Error) {
        trackError(err);
      }
    }
  }

  return (
    <>
      <section className="px-8 mb-4.5 w-full flex justify-between tablet:grid tablet:grid-cols-[auto_auto_1fr_auto]">
        <SearchBar
          label="Search asset"
          placeholder="Search asset"
          searchTerm={searchTerm}
          setSearchTerm={(term) => {
            setSearchTerm(term);
            selectFirstPage();
          }}
        />
        <DesktopFilters
          networks={orderTypeNetworks}
          values={filterState}
          showStatus={orderType === 'limit'}
          onChange={(filterState) => {
            dispatch({ type: FILTER_ACTIONS.UPDATE_ALL_FILTERS, payload: filterState });
            selectFirstPage();
            onFiltersChange(filterState);
          }}
        />
        <div className="flex items-center gap-3">
          {showReset && (
            <button
              onClick={onReset}
              className={buttons({
                size: 'small',
                shape: 'rounded',
                color: 'transparent',
              })}
            >
              {isMobile ? 'Reset' : 'Reset filters'}
            </button>
          )}
          <button
            className={buttons({
              size: 'small',
              shape: 'rounded',
              color: 'gray',
              class: ['ml-2', 'tablet:hidden', 'py-2.5'],
            })}
            onClick={() => setIsMobileFiltersOpen(true)}
          >
            Filters
          </button>
          <Modal
            isOpen={isMobileFiltersOpen}
            onOpenChange={setIsMobileFiltersOpen}
            onClose={() => setIsMobileFiltersOpen(false)}
          >
            <MobileFilters
              networks={orderTypeNetworks}
              defaultValues={filterState}
              showStatus={orderType === 'limit'}
              onSave={(nextFilterState) => {
                dispatch({ type: FILTER_ACTIONS.UPDATE_ALL_FILTERS, payload: nextFilterState });
                selectFirstPage();
                onFiltersChange(nextFilterState);
                setIsMobileFiltersOpen(false);
              }}
              onClose={() => setIsMobileFiltersOpen(false)}
            />
          </Modal>
        </div>
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                className={buttons({
                  size: 'small',
                  shape: 'rounded',
                  color: 'gray',
                  class: ['w-fit', 'ml-2', 'whitespace-nowrap', 'hidden', 'tablet:inline-block'],
                })}
                onClick={handleDownloadCsv}
                type="button"
              >
                Export
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                align="center"
                className="whitespace-nowrap text-sm min-w-max"
              >
                Max. 12,000 trades.
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </section>
      {isWaitingForPageData ? (
        <TableSkeleton numRows={PAGE_SIZE} columns={tableColumns} />
      ) : trades?.length ? (
        <DataTable
          table={table}
          columnClass={COMPLETED_TRADE_COLUMN_CLASS}
          isRowClickable={(row) => row.isClickable}
          onRowClick={(row) => {
            if (!row.originChainId || (!row.orderId && !row.transactionHash)) return;
            ampli.historyDetailViewed();
            handleShowTradeDetails({
              chainId: row.originChainId,
              destinationChainId: row.destinationChainId,
              liquiditySource: row.liquiditySource,
              orderId: row.orderId,
              transactionHash: row.transactionHash,
              funderAddress: row.funderAddress,
            });
          }}
        />
      ) : (
        <p className="text-center py-26">No trade history items to show.</p>
      )}
      {lastPage > 1 && (
        <div className="border-t-special-border-divider w-full border-t-1 py-4 tablet:pl-9">
          <Pagination {...paginationProps} lastPage={lastPage} />
        </div>
      )}
    </>
  );
};
